import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import PlanningFreightListPanel from "./planningFreightListPanel";
import PickupsFreightListPanel from "./pickupsFreightListPanel";
import CombinedFreightListPanel from "./combinedFreightListPanel";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { getPickupDetails } from "../../../services/pickups";
import { getPickup, pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import {
  getShipmentListDrawerTitle,
  isFreightListCategory,
  ShipmentListContext,
} from "../shipmentListTypes";

interface OutboundPlanningShipmentDrawerProps {
  context: ShipmentListContext | null;
  originTerminalNumber: number | undefined;
  onClose: () => void;
}

const OutboundPlanningShipmentDrawer: React.FC<OutboundPlanningShipmentDrawerProps> = ({
  context,
  originTerminalNumber,
  onClose,
}) => {
  const dispatch = useDispatch();
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState<string>("");
  const show = context != null && typeof originTerminalNumber === "number";

  const pickupDetails =
    pickupDetailsState.pickup && getPickupDetails(pickupDetailsState.pickup);

  const handleShowPickupDetails = useCallback(
    (pickupNumber: string) => {
      if (!pickupNumber) return;
      setPickupDetailsNumber(pickupNumber);
      dispatch(getPickup(pickupNumber));
    },
    [dispatch]
  );

  const handleClosePickupDetails = useCallback(() => {
    setPickupDetailsNumber("");
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setPickupDetailsNumber("");
    onClose();
  }, [onClose]);

  return (
    <>
      <SlideOutSidebar
        header={context ? getShipmentListDrawerTitle(context) : "Shipments"}
        show={show}
        onClose={handleCloseDrawer}
        size="large"
        className="outbound-planning-shipment-drawer"
      >
        {show && context && (
          context.category === "all" ? (
            <CombinedFreightListPanel
              key={context.fetchId}
              originTerminalNumber={originTerminalNumber}
              destinationTerminalNumbers={context.destinationTerminalNumbers}
              fetchId={context.fetchId}
              onPickupNumberClick={handleShowPickupDetails}
            />
          ) : isFreightListCategory(context.category) ? (
            <PlanningFreightListPanel
              key={context.fetchId}
              category={context.category}
              originTerminalNumber={originTerminalNumber}
              destinationTerminalNumbers={context.destinationTerminalNumbers}
              fetchId={context.fetchId}
            />
          ) : (
            <PickupsFreightListPanel
              key={context.fetchId}
              category={context.category}
              originTerminalNumber={originTerminalNumber}
              destinationTerminalNumbers={context.destinationTerminalNumbers}
              fetchId={context.fetchId}
              onPickupNumberClick={handleShowPickupDetails}
            />
          )
        )}
      </SlideOutSidebar>

      <PickupDetails
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={handleClosePickupDetails}
        isLoading={
          pickupDetailsState.requestStarted &&
          pickupDetailsState.requestCreator === "GET_PICKUP"
        }
        readonly
        error={
          pickupDetailsState.requestCreator === "GET_PICKUP"
            ? pickupDetailsState.requestError
            : ""
        }
      />
    </>
  );
};

export default OutboundPlanningShipmentDrawer;
